
import { useState, useEffect, useRef } from 'react'

import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'

import classNames from 'classnames'

import styles from './icd_codes_directory.module.sass'

import BlockWrapper from './layout/block_wrapper'
import ScrollAnimation from './layout/scroll_animation'
import Image from './basic/image'

import config from '../config'

function IcdCodesDirectory(props) {
	const searchContainerRef = useRef(null)
	const fields = props.fields
	const [activeCategory, setActiveCategory] = useState(null)
	const [codes, setCodes] = useState(fields.codes)
	const [filteredCodes, setFilteredCodes] = useState(fields.codes)
	const [toggleFilter, setToggleFilter] = useState(false)
	const categories = map(uniqBy(codes, (o) => o.category.value), function (item) {
		return {'label' :item.category.label, 'value' : item.category.value}
	})
	const [isMobile, setIsMobile] = useState(Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) < config.mobileWidth)

	const doSearch = () => {
		const searchContainer = searchContainerRef.current
		const searchInput = searchContainer.querySelector('input')
		const searchValue = searchInput.value.toLowerCase()
		const resetButton = searchContainer.querySelector('button[type="reset"]')
		if(searchValue.length > 0){
			if(activeCategory){
				setFilteredCodes(codes.filter(code => (code.code.toLowerCase().includes(searchValue) || code.description.toLowerCase().includes(searchValue)) && code.category.value == activeCategory))
			} else {
				setFilteredCodes(codes.filter(code => code.code.toLowerCase().includes(searchValue) || code.description.toLowerCase().includes(searchValue)))
			}
		} else {
			//if search input is empty, reset to active category
			if(activeCategory){
				setFilteredCodes(codes.filter(code => code.category.value == activeCategory))
			}else{
				setFilteredCodes(codes)
			}
		}

		//if there is a searchinput value, set reset to visible
		if(searchValue.length > 0){
			resetButton.style.display = 'block'
		} else {
			resetButton.style.display = 'none'
		}
	}

	const resetSearchInput = () => {
		const searchContainer = searchContainerRef.current
		const searchInput = searchContainer.querySelector('input')
		searchInput.value = ''
		const resetButton = searchContainer.querySelector('button[type="reset"]')
		if(activeCategory){
			setFilteredCodes(codes.filter(code => code.category.value == activeCategory))
		} else {
			setFilteredCodes(codes)
		}

		resetButton.style.display = 'none'
	}

	useEffect(() => {
		if(activeCategory){
			setFilteredCodes(codes.filter(code => code.category.value == activeCategory))
		}else{
			setFilteredCodes(codes)
		}

		//if search input has a value, do search
		const searchContainer = searchContainerRef.current
		const searchInput = searchContainer.querySelector('input')
		const searchValue = searchInput.value.toLowerCase()
		if(searchValue.length > 0){
			doSearch()
		}
	}, [activeCategory])

	//when toggle filter is clicked, set height of filter to calculated height
	useEffect(() => {
		const filter = document.querySelector(`.${styles.filter_wrapper}`)

		if(filter){
			if(toggleFilter){
				const height = filter.scrollHeight + 'px'

				filter.style.height = height
			} else {
				filter.style.height = '100px'
			}
		}
	}, [toggleFilter])

	useEffect(() => {
		//for each keypress in the search input, do a search
		const searchContainer = searchContainerRef.current
		const searchInput = searchContainer.querySelector('input')
		searchInput.addEventListener('keyup', doSearch)
		
		const handleResize = () => {
			setIsMobile(Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) < config.mobileWidth)
		}

		window.addEventListener('resize', handleResize)

		if(!isMobile){
			//on click of search input, set toggleFilter to false
			searchInput.addEventListener('click', () => {
				setToggleFilter(false)
			})
		}

		return () => {
			window.removeEventListener('resize', handleResize)

			if(!isMobile){
				searchInput.removeEventListener('keyup', doSearch)
			}
		}
	}, [activeCategory, isMobile])

	return <ScrollAnimation styles={styles}>
		<BlockWrapper {...props} className={styles.icd_codes_directory}>
			<div className={styles.directory_wrapper}>
				<div className={classNames(styles.search_bar_wrapper, toggleFilter ? styles.expanded : '')}>
					<div className={classNames(styles.filter_wrapper)}>
						<span className={styles.h5}><h2>Filter by:</h2></span>
						<div className={classNames(styles.filter)} onClick={() => setToggleFilter(!toggleFilter)}>
							<button className={classNames(styles.h3, styles.placeholder, !activeCategory ? styles.active : '')}>
								{!activeCategory ? 'Cancer Type' : categories.find(category => category.value == activeCategory).label}
							</button>
							<button onClick={() => setActiveCategory(null)} className={classNames(styles.h3)}><span></span>None</button>
							{categories.map((category, i) => {
								return <button key={i} className={classNames(styles.h3, styles.option, activeCategory == category.value ? styles.active : '')} onClick={() => setActiveCategory(category.value)}>
									<span>
										<Image fields={{image: {url: `/wp-content/themes/personalis/assets/icons/icd/${category.value}.svg`}}} />
									</span>{category.label}</button>
							})}
						</div>
					</div>
					<div ref={searchContainerRef} className={classNames(styles.search_container)}>
						<h2><span>Search:</span></h2>
						<div className={styles.search_input_wrapper}>
							<input type="text" />
							<button onClick={resetSearchInput} type="reset" className={classNames(styles.reset)} />
						</div>
					</div>
					<button onClick={doSearch} className={classNames(styles.search_submit)}>Submit</button>
				</div>
				
				<div className={styles.directory}>
					<div className={styles.directory_header}>
						<div className={styles.h5}>Code</div>
						<div className={styles.h5}>Cancer Type {isMobile && `& Description`}</div>
						{!isMobile && <div className={styles.h5}>Description</div>}
					</div>
					<div className={classNames(styles.codes_wrapper)}>
						{filteredCodes && filteredCodes.map((code, i) => {
							return <div key={i} className={styles.code}>
								<div className={styles.code_title}>{code.code}</div>
								<div className={styles.code_category}>{code.subcategory ? code.subcategory : code.category.label}</div>
								<div className={styles.code_description}>{code.description}</div>
							</div>
						})}

						{filteredCodes.length == 0 && <div className={classNames(styles.code, styles.no_code)}>No codes were found.</div>}
					</div>
				</div>
			</div>
		</BlockWrapper>
	</ScrollAnimation>
}

export default IcdCodesDirectory